import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServiceCard from "../components/ServiceCard";
import LocationPicker, { readSavedLocation } from "../components/LocationPicker";
import { API, useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/seo";
import { fetchCategories, toCategorySlug, categoryLabel, visibleCategories } from "../lib/categories";

const RADIUS_OPTIONS = [5, 10, 25, 50, 100];

// Fallback chips shown to anonymous users only. Signed-in users see
// personalized suggestions from their own history instead.
const SUGGESTED = ["auto detailing", "house cleaning", "haircut", "personal training", "wedding photographer", "pet grooming"];

export default function Services() {
  const [params, setParams] = useSearchParams();
  const { user, authHeader } = useAuth();
  const [q, setQ] = useState(params.get("q") || "");
  const [categories, setCategories] = useState([]);
  // `cat` is always a canonical slug ("" = All). Legacy label URLs still work.
  const [cat, setCat] = useState(() => toCategorySlug(params.get("category")));
  const catLabel = categoryLabel(cat, categories);

  useSeo({
    title: catLabel
      ? `${catLabel} services — BookFreely`
      : "Browse local service businesses — BookFreely",
    description:
      "Search and compare independent local service businesses on BookFreely. Filter by category, distance, and availability, and book online with transparent deposits.",
    path: "/services",
  });
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(() => readSavedLocation());
  const [radius, setRadius] = useState(() => {
    const r = parseInt(params.get("radius") || "25", 10);
    return Number.isFinite(r) ? r : 25;
  });
  const [openNow, setOpenNow] = useState(params.get("open_now") === "1");
  const [recent, setRecent] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    fetchCategories().then((cs) => {
      setCategories(cs);
      setCat((c) => toCategorySlug(c, cs));
    });
  }, []);

  const load = async () => {
    setLoading(true);
    const req = { q: q || undefined, category: cat || undefined };
    if (location && typeof location.latitude === "number" && typeof location.longitude === "number") {
      req.lat = location.latitude;
      req.lng = location.longitude;
      req.radius_miles = radius;
    }
    if (openNow) req.open_now = true;
    try {
      const r = await axios.get(`${API}/services`, { params: req });
      setServices(r.data);
    } catch {
      setServices([]);
    }
    setLoading(false);
  };

  // Fetch the signed-in user's personal recent + personalized suggestions on mount.
  // Anonymous users see only the static SUGGESTED fallback chips.
  const refreshPersonal = React.useCallback(() => {
    if (!user) { setRecent([]); setSuggestions([]); return; }
    axios.get(`${API}/search/recent`, { headers: authHeader(), params: { limit: 8 } })
      .then((r) => setRecent(r.data.recent || [])).catch(() => setRecent([]));
    axios.get(`${API}/search/suggestions`, { headers: authHeader(), params: { limit: 8 } })
      .then((r) => setSuggestions(r.data.suggestions || [])).catch(() => setSuggestions([]));
  }, [user, authHeader]);

  useEffect(() => { refreshPersonal(); }, [refreshPersonal]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, location?.latitude, location?.longitude, radius, openNow]);

  const onSearch = (val) => {
    setQ(val);
    setParams((p) => {
      const np = new URLSearchParams(p);
      if (val) np.set("q", val); else np.delete("q");
      return np;
    });
    setTimeout(() => {
      load();
      // Track for the signed-in user's private recent list only.
      if (val && val.trim().length >= 2 && user) {
        axios.post(`${API}/search/track`, { q: val.trim() }, { headers: authHeader() })
          .then(() => refreshPersonal())
          .catch(() => {});
      }
    }, 0);
  };

  const applyChip = (term) => {
    setQ(term);
    onSearch(term);
  };

  const clearRecent = async () => {
    if (!user) return;
    try {
      await axios.delete(`${API}/search/recent`, { headers: authHeader() });
      setRecent([]);
      refreshPersonal();
    } catch {}
  };

  const removeRecent = async (id) => {
    if (!user || !id) return;
    // optimistic
    setRecent((rs) => rs.filter((r) => r.id !== id));
    try {
      await axios.delete(`${API}/search/recent/${id}`, { headers: authHeader() });
      refreshPersonal();
    } catch {
      refreshPersonal();
    }
  };

  const selectCategory = (slug) => {
    setCat(slug);
    setParams((p) => {
      const np = new URLSearchParams(p);
      if (slug) np.set("category", slug); else np.delete("category");
      return np;
    });
  };

  const hasFilters = !!(q || cat || openNow || location);

  const resetFilters = () => {
    setQ("");
    setCat("");
    setOpenNow(false);
    setParams(new URLSearchParams());
    setTimeout(load, 0);
  };

  const toggleOpenNow = () => {
    setOpenNow((v) => {
      const nv = !v;
      setParams((p) => {
        const np = new URLSearchParams(p);
        if (nv) np.set("open_now", "1"); else np.delete("open_now");
        return np;
      });
      return nv;
    });
  };

  return (
    <div className="App">
      <Header onSearch={onSearch} searchValue={q} setSearchValue={setQ} />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Marketplace</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "var(--gf-text)" }}>
          {location ? "Services near you" : "Browse services"}
        </h1>

        {/* Recent + personalized suggestions.
             - Signed-in users: their private recent searches + personalized "For you" chips (categories from bookings/favorites).
             - Anonymous users: a small static "Try" fallback list. No cross-user data. */}
        {((user && (recent.length > 0 || suggestions.length > 0)) || (!user && !q)) && (
          <div className="mt-6 space-y-3" data-testid="search-suggestions">
            {user && recent.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="recent-chips">
                <span className="text-[10px] uppercase tracking-[0.2em] flex-shrink-0" style={{ color: "var(--gf-text-muted)" }}>Recent</span>
                {recent.map((r, i) => (
                  <span
                    key={r.id || `${r.q}-${i}`}
                    className="inline-flex items-center rounded-full border overflow-hidden"
                    style={{ borderColor: "var(--gf-border-strong)" }}
                  >
                    <button
                      data-testid={`recent-chip-${i}`}
                      onClick={() => applyChip(r.q)}
                      className="px-3 py-1.5 text-xs hover:opacity-80"
                      style={{ color: "var(--gf-text)" }}
                    >
                      {r.q}
                    </button>
                    <button
                      data-testid={`recent-chip-remove-${i}`}
                      onClick={() => removeRecent(r.id)}
                      aria-label={`Remove ${r.q} from recent searches`}
                      title="Remove"
                      className="pr-2 pl-1 py-1.5 text-xs hover:opacity-80"
                      style={{ color: "var(--gf-text-muted)" }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  data-testid="clear-recent-btn"
                  onClick={clearRecent}
                  className="text-[10px] underline ml-1"
                  style={{ color: "var(--gf-text-muted)" }}
                >
                  Clear all
                </button>
              </div>
            )}
            {user && suggestions.filter((s) => s.kind !== "recent").length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="foryou-chips">
                <span className="text-[10px] uppercase tracking-[0.2em] flex-shrink-0" style={{ color: "var(--gf-accent)" }}>For you</span>
                {suggestions.filter((s) => s.kind !== "recent").map((t, i) => (
                  <button
                    key={`${t.q}-${i}`}
                    data-testid={`foryou-chip-${i}`}
                    onClick={() => applyChip(t.q)}
                    className="rounded-full px-3 py-1.5 text-xs"
                    style={{ background: "var(--gf-surface-2)", color: "var(--gf-accent)" }}
                  >
                    {t.q}
                  </button>
                ))}
              </div>
            )}
            {!user && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="suggested-chips">
                <span className="text-[10px] uppercase tracking-[0.2em] flex-shrink-0" style={{ color: "var(--gf-text-muted)" }}>Try</span>
                {SUGGESTED.map((s, i) => (
                  <button
                    key={s}
                    data-testid={`suggested-chip-${i}`}
                    onClick={() => applyChip(s)}
                    className="rounded-full border px-3 py-1.5 text-xs hover:opacity-80"
                    style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 rounded-2xl border p-4" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
          <div className="text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Location</div>
          <LocationPicker value={location} onChange={setLocation} />
          {location && (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="radius-filter">
              <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Within</span>
              {RADIUS_OPTIONS.map((r) => (
                <button key={r}
                  data-testid={`radius-${r}`}
                  onClick={() => setRadius(r)}
                  className="rounded-full px-3 py-1 text-xs border transition-colors"
                  style={radius === r
                    ? { background: "var(--gf-accent)", color: "var(--gf-on-accent)", borderColor: "var(--gf-accent)" }
                    : { background: "transparent", color: "var(--gf-text)", borderColor: "var(--gf-border-strong)" }}>
                  {r} mi
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap flex-1" data-testid="category-filters">
            {[{ slug: "", label: "All Services" }, ...visibleCategories(categories)].map((c) => {
              const active = cat === c.slug;
              return (
                <button
                  key={c.slug || "all"}
                  data-testid={`category-${c.slug || "all"}`}
                  onClick={() => selectCategory(c.slug)}
                  className="px-4 py-2 rounded-full border text-sm transition-colors"
                  style={{
                    background: active ? "var(--gf-accent)" : "transparent",
                    color: active ? "var(--gf-on-accent)" : "var(--gf-text)",
                    borderColor: active ? "var(--gf-accent)" : "var(--gf-border-strong)",
                  }}
                >
                  {c.label}
                  {typeof c.count === "number" && c.slug ? (
                    <span className="ml-1.5 text-[11px] opacity-70">{c.count}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <button
            data-testid="open-now-toggle"
            onClick={toggleOpenNow}
            className="px-4 py-2 rounded-full border text-sm transition-colors flex items-center gap-1.5"
            style={openNow
              ? { background: "var(--gf-accent)", color: "var(--gf-on-accent)", borderColor: "var(--gf-accent)" }
              : { background: "transparent", color: "var(--gf-text)", borderColor: "var(--gf-border-strong)" }}
            title="Show only services with availability right now"
          >
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: openNow ? "var(--gf-on-accent)" : "var(--gf-accent)" }} />
            Open now
          </button>
        </div>

        <div className="mt-10">
          {loading ? (
            <div style={{ color: "var(--gf-text-muted)" }}>Loading...</div>
          ) : services.length === 0 ? (
            <div data-testid="empty-services" className="text-center py-16 rounded-2xl border" style={{ borderColor: "var(--gf-border)" }}>
              <div className="font-display text-2xl" style={{ color: "var(--gf-text)" }}>
                No services match your filters
              </div>
              <p className="mt-2 px-4 text-sm" style={{ color: "var(--gf-text-muted)" }}>
                {[
                  q ? `search "${q}"` : null,
                  catLabel ? `category ${catLabel}` : null,
                  openNow ? "open right now" : null,
                  location ? `within ${radius} mi` : null,
                ].filter(Boolean).length > 0
                  ? `Nothing found for ${[
                      q ? `“${q}”` : null,
                      catLabel ? catLabel : null,
                      openNow ? "open now" : null,
                      location ? `${radius} mi radius` : null,
                    ].filter(Boolean).join(" · ")}. Try widening your filters.`
                  : "There are no published services here yet. Check back soon."}
              </p>
              <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
                {q && cat && (
                  <button data-testid="clear-category-keep-query-btn" onClick={() => selectCategory("")}
                    className="rounded-full px-5 py-2 text-sm font-medium"
                    style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                    Search all categories for &ldquo;{q}&rdquo;
                  </button>
                )}
                {hasFilters && (
                  <button data-testid="reset-filters-btn" onClick={resetFilters}
                    className="rounded-full border px-5 py-2 text-sm font-medium"
                    style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
                    Reset filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
              {services.map((s) => <ServiceCard key={s.id} s={s} />)}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
